import React, { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FloppyDisk, Key, LockKey, User as UserIcon, WarningCircle } from "@phosphor-icons/react";

/**
 * My Profile — self-service view for the signed-in user.
 *
 * Renders the SAME sections that supervisors/admins see on a member's
 * profile page (`/members/:id`) so the member can review every piece
 * of data BSVAC has on file for them. Every operational field is
 * READ-ONLY here regardless of role — even a supervisor cannot edit
 * their own seniority, uniform issue dates, ambulance clearance or
 * training attendance from this page (those changes go through the
 * appropriate admin flows). Only three things are editable:
 *   • Name
 *   • Email address
 *   • Password (via /auth/change-my-password)
 *
 * The old /settings page is kept at the same route so links don't
 * break — the sidebar entry is now labelled "My Profile" and the page
 * header reflects that as well.
 */

const AMBULANCES = ["BSVAC-2", "BSVAC-4", "BSVAC-5", "CBVAC-6", "BSVAC-7"];

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d + "T12:00:00").toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return d; }
}
function daysUntil(d) {
  if (!d) return null;
  const t = new Date(d + "T12:00:00").getTime();
  return Math.round((t - Date.now()) / 86400000);
}
function ExpiryTag({ date }) {
  if (!date) return <span className="text-slate-400">—</span>;
  const days = daysUntil(date);
  const label = fmtDate(date);
  if (days == null) return <span className="mono text-xs">{label}</span>;
  let cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
  let hint = `${days}d left`;
  if (days < 0) { cls = "bg-red-50 text-red-800 border-red-200"; hint = `expired ${Math.abs(days)}d ago`; }
  else if (days < 30) { cls = "bg-amber-50 text-amber-800 border-amber-200"; }
  return (
    <span className={`inline-flex items-center gap-1.5 border rounded-sm px-1.5 py-0.5 mono text-[11px] ${cls}`}>
      {days < 0 && <WarningCircle size={12} weight="fill" />}
      {label}
      <span className="opacity-60">· {hint}</span>
    </span>
  );
}

function Section({ title, subtitle, children, right }) {
  return (
    <section className="surface-grid mb-5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <div className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <Label className="tab-tag">{label}</Label>
      {children}
      {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
    </div>
  );
}

async function downsizeImageToDataUrl(file, maxEdge = 640, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function ProfilePhoto({ doc, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    setBusy(true);
    try {
      const dataUrl = await downsizeImageToDataUrl(file);
      const { data } = await api.post("/users/me/photo", { photo_data_url: dataUrl });
      toast.success("Photo updated");
      onUpdated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  }
  async function removePhoto() {
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/users/me/photo");
      toast.success("Photo removed");
      onUpdated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  const initials = (doc.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="flex flex-col items-center gap-2 shrink-0" data-testid="profile-photo">
      {doc.photo_url ? (
        <img src={doc.photo_url} alt={doc.name} className="w-28 h-28 rounded-sm object-cover border border-slate-200" data-testid="profile-photo-img" />
      ) : (
        <div className="w-28 h-28 rounded-sm border border-slate-200 bg-slate-100 flex items-center justify-center font-display font-bold text-3xl text-slate-500" data-testid="profile-photo-placeholder">
          {initials}
        </div>
      )}
      <div className="flex flex-col gap-1 w-28">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="profile-photo-file" />
        <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="profile-photo-camera" />
        <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-[10px]" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="profile-photo-upload-btn">Upload</Button>
        <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-[10px]" onClick={() => camRef.current?.click()} disabled={busy} data-testid="profile-photo-camera-btn">Take Photo</Button>
        {doc.photo_url && (
          <Button type="button" size="sm" variant="ghost" className="rounded-sm h-6 text-[10px] text-red-600 hover:bg-red-50" onClick={removePhoto} disabled={busy} data-testid="profile-photo-remove-btn">Remove</Button>
        )}
      </div>
    </div>
  );
}

/** Read-only date display used everywhere below. Members can look but
 * not touch — every operational date is managed by supervisors on the
 * `/members/:id` page. */
function ReadOnlyDate({ value, testid }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50 h-10 px-2 flex items-center text-sm mono" data-testid={testid}>
      {value ? fmtDate(value) : <span className="text-slate-400">—</span>}
    </div>
  );
}

const CLINICAL = new Set(["EMT", "Paramedic", "Intern"]);
const TRAINING_ELIGIBLE = new Set(["MVO", "EMT", "Paramedic", "Intern"]);

export default function Settings() {
  const { user, setUser } = useAuth();
  const [doc, setDoc] = useState(null);
  const [training, setTraining] = useState(null);
  const [classMap, setClassMap] = useState({});
  const [form, setForm] = useState({ name: "", email: "" });
  const [dirty, setDirty] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    Promise.all([
      api.get(`/users/${user.id}`).catch(() => ({ data: null })),
      api.get("/training/completion/me").catch(() => ({ data: null })),
      api.get("/training/classes?include_inactive=true").catch(() => ({ data: [] })),
    ]).then(([u, tc, cls]) => {
      if (!alive) return;
      const d = u.data || user;
      setDoc(d);
      setForm({ name: d.name || "", email: d.email || "" });
      setTraining(tc.data || null);
      setClassMap(Object.fromEntries((cls.data || []).map((x) => [x.id, x.name])));
    });
    return () => { alive = false; };
  }, [user?.id]);

  const memberRoles = useMemo(() => {
    if (!doc) return new Set();
    const certs = doc.certifications?.length ? doc.certifications : [doc.certification].filter(Boolean);
    return new Set(certs);
  }, [doc]);
  const isMVO = memberRoles.has("MVO");
  const isClinical = [...memberRoles].some((c) => CLINICAL.has(c));
  const showTraining = (training?.classes?.length || 0) > 0 || [...memberRoles].some((c) => TRAINING_ELIGIBLE.has(c));

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); setDirty(true); }

  async function saveIdentity(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setProfileBusy(true);
    try {
      const payload = {};
      if (form.name !== (doc?.name || "")) payload.name = form.name.trim();
      if (form.email !== (doc?.email || "")) payload.email = form.email.trim();
      if (!Object.keys(payload).length) { setDirty(false); return; }
      const { data } = await api.patch("/auth/me", payload);
      setDoc((d) => ({ ...(d || {}), ...data }));
      if (setUser) setUser(data);
      setDirty(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setProfileBusy(false); }
  }

  async function changePassword(e) {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-my-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally { setPwBusy(false); }
  }

  if (!doc) {
    return <AppLayout title="My Profile"><div className="p-6 text-sm text-slate-500">Loading…</div></AppLayout>;
  }

  return (
    <AppLayout
      title="My Profile"
      subtitle="Your account and operational record — only name, email and password are editable here"
    >
      {/* ============ Basic info + photo ============ */}
      <Section title="Basic Information" subtitle="Photo, name, contact and rank">
        <div className="flex flex-col md:flex-row gap-6">
          <ProfilePhoto
            doc={doc}
            onUpdated={(u) => {
              setDoc((d) => ({ ...d, photo_url: u.photo_url }));
              if (setUser && user) setUser({ ...user, photo_url: u.photo_url });
            }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm flex-1">
            <Field label="Name" testid="profile-name">
              <Input
                value={form.name}
                onChange={(e) => upd("name", e.target.value)}
                className="rounded-sm"
                data-testid="profile-name-input"
              />
            </Field>
            <Field label="Email" hint="Used for login and system notifications" testid="profile-email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => upd("email", e.target.value)}
                className="rounded-sm"
                data-testid="profile-email-input"
              />
            </Field>
            <div>
              <div className="tab-tag text-slate-500">Phone</div>
              <div className="mono text-xs mt-1">{doc.phone || "—"}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Contact an administrator to update.</div>
            </div>
            <div>
              <div className="tab-tag text-slate-500">Role</div>
              <div className="mt-1">
                <Badge variant="outline" className="rounded-sm mr-1">{doc.role}</Badge>
                {(doc.additional_roles || []).map((r) => <Badge key={r} variant="outline" className="rounded-sm mr-1">{r}</Badge>)}
              </div>
            </div>
            <div>
              <div className="tab-tag text-slate-500">Certifications / Rank</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {[...memberRoles].length === 0 ? <span className="text-xs text-slate-400">—</span> :
                  [...memberRoles].map((c) => <Badge key={c} className="rounded-sm bg-slate-900 text-white">{c}</Badge>)}
              </div>
            </div>
            <div>
              <div className="tab-tag text-slate-500">Status</div>
              <div className="mt-1">
                {doc.active === false
                  ? <Badge className="rounded-sm bg-slate-400 text-white">Inactive</Badge>
                  : <Badge className="rounded-sm bg-emerald-600 text-white">Active</Badge>}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={saveIdentity}
            disabled={!dirty || profileBusy}
            className="rounded-sm bg-slate-900 text-white disabled:opacity-40"
            data-testid="profile-save-identity"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {profileBusy ? "Saving…" : "Save Name / Email"}
          </Button>
        </div>
      </Section>

      {/* ============ Password ============ */}
      <form onSubmit={changePassword}>
        <Section title="Change Password" subtitle="At least 8 characters — you'll stay signed in on this device">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
            <Field label="Current Password" testid="profile-pw-current">
              <Input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="rounded-sm" data-testid="profile-pw-current-input" />
            </Field>
            <Field label="New Password" testid="profile-pw-new">
              <Input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="rounded-sm" data-testid="profile-pw-new-input" />
            </Field>
            <Field label="Confirm New Password" testid="profile-pw-confirm">
              <Input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="rounded-sm" data-testid="profile-pw-confirm-input" />
            </Field>
          </div>
          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={pwBusy || !pw.current || !pw.next || !pw.confirm} className="rounded-sm bg-slate-900 text-white disabled:opacity-40" data-testid="profile-save-password">
              <Key size={14} weight="bold" className="mr-1" />
              {pwBusy ? "Updating…" : "Update Password"}
            </Button>
          </div>
        </Section>
      </form>

      {/* Ambulance Clearance removed in iter34.3 — ride-eligibility
          now lives on Clinical Clearance (N/I/C/C+/FTO). */}

      {/* ============ Vehicle Clearance (MVO) ============ */}
      {isMVO && (
        <Section title="Vehicle Clearance" subtitle="Ambulances you are authorized to operate — set by an administrator">
          <div className="space-y-3">
            <Field label="Driver Clearance Date" testid="profile-driver-clear-date">
              <div className="max-w-xs"><ReadOnlyDate value={doc.driver_clearance_date} testid="profile-driver-clear-date-val" /></div>
            </Field>
            <div>
              <Label className="tab-tag mb-1 block">Vehicles Cleared</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 max-w-xl">
                {AMBULANCES.map((u) => {
                  const on = (doc.cleared_to_drive || []).includes(u);
                  return (
                    <label key={u} className={`flex items-center gap-2 text-sm border rounded-sm px-2 py-2 opacity-90 ${on ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`} data-testid={`profile-drive-${u}`}>
                      <Checkbox checked={on} disabled />
                      <span className="mono text-xs">{u}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ============ Driver Restriction (MVO, read-only) ============ */}
      {isMVO && (
        <Section title="Driver Restriction" subtitle="Set by a supervisor. While active, you cannot be assigned to MVO tours.">
          {doc.driver_restriction && doc.driver_restriction.active ? (
            <div className="p-3 border border-red-200 rounded-sm bg-red-50 text-sm" data-testid="profile-restriction-active">
              <div className="font-bold text-red-800 mb-1">RESTRICTED</div>
              <div className="text-red-800">
                <span className="mono">{doc.driver_restriction.start_date}</span>
                {" → "}
                {doc.driver_restriction.end_date ? <span className="mono">{doc.driver_restriction.end_date}</span> : <span className="italic">Indefinite</span>}
                {" · "}{doc.driver_restriction.duration}
              </div>
              <div className="text-[11px] text-red-700 mt-1">
                Contact a supervisor with any questions.
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic" data-testid="profile-restriction-none">No active driver restriction.</div>
          )}
        </Section>
      )}

      {/* ============ Seniority (read-only) ============ */}
      <Section title="Seniority" subtitle="Membership dates — managed by an administrator">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
          <Field label="Start Date" testid="profile-sen-start"><ReadOnlyDate value={doc.seniority_start_date} testid="profile-sen-start-val" /></Field>
          <Field label="End Date" testid="profile-sen-end"><ReadOnlyDate value={doc.seniority_end_date} testid="profile-sen-end-val" /></Field>
        </div>
      </Section>

      {/* ============ Uniform (read-only) ============ */}
      <Section title="Uniform" subtitle="Shirt and hat issued — managed by an administrator">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-3">
            <div className="tab-tag">Shirt</div>
            <Field label="Size" testid="profile-shirt-size">
              <div className="border border-slate-200 rounded-sm bg-white h-10 px-2 flex items-center text-sm mono" data-testid="profile-shirt-size-val">
                {doc.shirt_size || <span className="text-slate-400">—</span>}
              </div>
            </Field>
            <Field label="Date Received" testid="profile-shirt-date"><ReadOnlyDate value={doc.shirt_received_date} testid="profile-shirt-date-val" /></Field>
          </div>
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-3">
            <div className="tab-tag">Hat</div>
            <label className="flex items-center gap-2 text-sm opacity-90" data-testid="profile-hat-issued">
              <Checkbox checked={!!doc.hat_issued} disabled />
              <span>Hat issued</span>
            </label>
            <Field label="Date Received" testid="profile-hat-date"><ReadOnlyDate value={doc.hat_received_date} testid="profile-hat-date-val" /></Field>
          </div>
        </div>
      </Section>

      {/* ============ Training (read-only) ============ */}
      {showTraining && (
        <Section
          title="Training"
          subtitle="Required classes — attendance is submitted by instructors and cannot be edited here"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="tab-tag">Required Classes</div>
                {training && (
                  <span className="text-[11px] mono text-slate-500" data-testid="profile-training-pct">
                    {training.completed_count}/{training.required_count} · {training.percent_complete}%
                  </span>
                )}
              </div>
              {!training || (training.classes || []).length === 0 ? (
                <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-3">
                  No required classes for your classification yet.
                </div>
              ) : (
                <ul className="space-y-1 text-sm" data-testid="profile-required-classes">
                  {training.classes.map((c) => (
                    <li
                      key={c.class_id}
                      className={`flex items-center justify-between border rounded-sm px-2 py-1.5 ${c.completed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
                      data-testid={`profile-required-class-${c.class_id}`}
                    >
                      <span className="flex items-center gap-2">
                        <Checkbox checked={!!c.completed} disabled />
                        <span className={`font-medium ${c.completed ? "text-slate-900" : "text-red-800"}`}>{c.class_name}</span>
                      </span>
                      <span className={`mono text-[11px] ${c.completed ? "text-slate-600" : "text-red-700 font-bold"}`}>
                        {c.completed ? fmtDate(c.date) : "OUTSTANDING"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {training && (training.outstanding || []).length > 0 && (
                <div className="mt-2 text-[11px] text-red-700" data-testid="profile-outstanding-summary">
                  Outstanding: {training.outstanding.join(", ")}
                </div>
              )}
            </div>
            <div>
              <div className="tab-tag mb-2">Authorized to Teach</div>
              {(!doc.instructor_classes || doc.instructor_classes.length === 0) ? (
                <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-3">
                  Not currently an instructor.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {doc.instructor_classes.map((cid) => <Badge key={cid} className="rounded-sm bg-slate-900 text-white">{classMap[cid] || cid}</Badge>)}
                </div>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* ============ Managed-by-admin footer ============ */}
      <Section title="Account Details" subtitle="Contact a Chief or Supervisor if any of these need to change" >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="tab-tag text-slate-500">Username</div>
            <div className="mono text-xs mt-1">{doc.username || "—"}</div>
          </div>
          <div>
            <div className="tab-tag text-slate-500">Additional Roles</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {(doc.additional_roles || []).length === 0
                ? <span className="text-xs text-slate-400">None</span>
                : doc.additional_roles.map((r) => <Badge key={r} variant="outline" className="rounded-sm">{r}</Badge>)}
            </div>
          </div>
          {isClinical && doc.medical_clearance_date && (
            <div>
              <div className="tab-tag text-slate-500">Medical Clearance</div>
              <div className="mt-1"><ExpiryTag date={doc.medical_clearance_date} /></div>
            </div>
          )}
          {isClinical && doc.nys_igel_cert_date && (
            <div>
              <div className="tab-tag text-slate-500">NYS iGEL Certification</div>
              <div className="mt-1"><ExpiryTag date={doc.nys_igel_cert_date} /></div>
            </div>
          )}
        </div>
      </Section>
    </AppLayout>
  );
}
